import Reveal from "../lib/Reveal";

/* Ícones */
function IconCheck({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function IconSpark({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M12 2l1.8 4.7L18 8.5l-4.2 1.8L12 15l-1.8-4.7L6 8.5l4.2-1.8L12 2z" />
    </svg>
  );
}

type Tier = {
  name: string;
  price: string;
  features: string[];
  highlight?: boolean;
  note?: string;
};

const tiers: Tier[] = [
  { name: "Start", price: "R$ 49,99", features: ["Ativação por unidade", "Suporte comercial", "Internet 300 Mb/s"], note: "Perfeito para começar simples e previsível." },
  { name: "Pro", price: "R$ 89,90", features: ["Suporte 24/7", "Internet 600 Mb/s", "Streaming incluso"], highlight: true, note: "Equilíbrio ideal entre desempenho e atendimento." },
  { name: "Enterprise", price: "Sob consulta", features: ["Projeto dedicado", "SLA avançado", "Edge & redundância"], note: "Arquitetura sob medida para alta criticidade." },
];

export default function Pricing() {
  return (
    <section
      id="planos"
      className="
        relative isolate overflow-hidden
        py-16 md:py-20
        bg-background
      "
    >
      {/* === GeometricFocusBG (inline) — sem borda/texture === */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          // máscara vertical para o fade suave ao tocar outras seções
          maskImage: "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)",
        }}
      >
        {/* Feixe esquerdo */}
        <div
          className="absolute -left-12 top-1/3 h-[120%] w-[120%] -rotate-[10deg]"
          style={{
            background: "conic-gradient(from 200deg at 20% 50%, rgba(255,0,100,.35) 0deg, rgba(255,0,100,0) 60deg)",
            filter: "blur(30px)",
            maskImage: "linear-gradient(to right, transparent, black 20%, black 70%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 20%, black 70%, transparent)",
          }}
        />
        {/* Feixe direito */}
        <div
          className="absolute right-[-15%] top-[55%] h-[120%] w-[120%] rotate-[8deg]"
          style={{
            background: "conic-gradient(from 140deg at 80% 40%, rgba(255,77,145,.30) 0deg, rgba(255,77,145,0) 70deg)",
            filter: "blur(34px)",
            maskImage: "linear-gradient(to left, transparent, black 15%, black 80%, transparent)",
            WebkitMaskImage: "linear-gradient(to left, transparent, black 15%, black 80%, transparent)",
          }}
        />
        {/* Foco radial central */}
        <div
          className="absolute left-1/2 top-[68%] -translate-x-1/2 -translate-y-1/2 h-[44rem] w-[58rem]"
          style={{
            background: "radial-gradient(60% 40% at 55% 50%, rgba(255,0,100,.30) 0%, rgba(255,0,100,0) 65%)",
            filter: "blur(22px)",
          }}
        />
      </div>
      {/* === /GeometricFocusBG === */}

      <div className="relative max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-extrabold">Planos flexíveis</h2>
          <p className="text-white/70 mt-2">
            Pague por unidade ativa. Sem custo de implantação para o condomínio.
          </p>
        </div>

        <Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {tiers.map((t, i) => {
              const cardBase =
                "relative rounded-3xl p-6 backdrop-blur-md bg-white/5 ring-1 ring-white/10 shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:ring-white/20 focus-within:ring-white/20";
              const highlightWrap =
                "bg-gradient-to-br from-[#ff0064]/30 via-[#ff4d91]/25 to-transparent p-[1px] rounded-3xl";

              const content = (
                <div className={cardBase} aria-label={`Plano ${t.name}`}>
                  {t.highlight && (
                    <div className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-black/60 ring-1 ring-white/20 px-3 py-1 text-xs font-semibold">
                      <IconSpark className="h-3.5 w-3.5 text-white/90" />
                      <span>Mais popular</span>
                    </div>
                  )}

                  <h3 className="text-lg font-semibold">{t.name}</h3>

                  <p
                    className="mt-2 text-3xl font-extrabold leading-tight"
                    style={{
                      background: "linear-gradient(90deg,#FF0064,#ff4d91)",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    {t.price}
                  </p>

                  {t.note && <p className="mt-1 text-xs text-white/60">{t.note}</p>}

                  <ul className="mt-4 space-y-2 text-sm">
                    {t.features.map((f, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-white/85">
                        <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/10">
                          <IconCheck />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-6">
                    <a
                      href="#cta"
                      className={
                        "inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold ring-1 transition " +
                        (t.highlight
                          ? "bg-white text-black ring-white/10 hover:bg-white/90"
                          : "bg-black/40 text-white ring-white/20 hover:bg-black/55")
                      }
                    >
                      Quero este
                    </a>
                  </div>

                  <p className="mt-3 text-[11px] leading-relaxed text-white/50">
                    Sem fidelidade. Cancelamento a qualquer momento. Valores por unidade ativa.
                  </p>
                </div>
              );

              return <div key={i} className={t.highlight ? highlightWrap : ""}>{content}</div>;
            })}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
